import { css, cx } from '@/styled-system/css';
import { useState } from 'react';
import { CaretDownIcon } from '../icons';
import {
  caretIcon,
  caretIconRotated,
  readMoreButton,
  readMoreContent,
  seoContentContainer,
  visibleContent,
} from './readMoreButton.styles';
import { useReadMore, type UseReadMoreOptions } from './useReadMore';

// Controlled API (existing, maintained for backward compatibility)
export type ReadMoreButtonControlledProps = {
  /** Whether content is currently expanded (controlled component) */
  isExpanded: boolean;
  /** Click handler for toggling state (controlled component) */
  onClick: () => void;
  /** Custom button text (optional) */
  children?: React.ReactNode;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Custom aria-label for accessibility */
  'aria-label'?: string;
};

// All-in-one API (new, recommended for most cases)
export type ReadMoreButtonAllInOneProps = {
  /** Text content to display with read more/less functionality */
  content: string;
  /** Maximum number of lines to show when collapsed */
  maxLines?: number;
  /** CSS class to apply to content container */
  className?: string;
  /** Estimated characters per line for overflow detection */
  estimatedLineLength?: number;
  /** Custom character threshold for determining truncation */
  characterThreshold?: number;
  /** Custom button text when collapsed */
  readMoreText?: string;
  /** Custom button text when expanded */
  readLessText?: string;
  /** Custom aria-label for accessibility */
  'aria-label'?: string;
  /** Whether button is disabled */
  disabled?: boolean;
};

// SEO-friendly API (Constellation V10 ShowHide pattern) - Manual split
export type ReadMoreButtonSEOProps = {
  /** Always visible content part */
  truncatedContent: string;
  /** Content hidden visually but present in HTML for SEO */
  hiddenContent: string;
  /** CSS class to apply to content container */
  className?: string;
  /** Custom button text when collapsed */
  readMoreText?: string;
  /** Custom button text when expanded */
  readLessText?: string;
  /** Custom aria-label for accessibility */
  'aria-label'?: string;
  /** Whether button is disabled */
  disabled?: boolean;
};

// Auto-split SEO API (Recommended) - Component handles splitting
export type ReadMoreButtonAutoSplitProps = {
  /** Full content that will be automatically split for SEO-friendly display */
  content: string;
  /** Maximum length for truncated portion (default: 150 characters) */
  maxTruncatedLength?: number;
  /** CSS class to apply to content container */
  className?: string;
  /** Custom button text when collapsed */
  readMoreText?: string;
  /** Custom button text when expanded */
  readLessText?: string;
  /** Custom aria-label for accessibility */
  'aria-label'?: string;
  /** Whether button is disabled */
  disabled?: boolean;
};

export type ReadMoreButtonProps =
  | ReadMoreButtonControlledProps
  | ReadMoreButtonAllInOneProps
  | ReadMoreButtonSEOProps
  | ReadMoreButtonAutoSplitProps;

/**
 * Type guards to determine which API is being used
 */
function isControlledProps(props: ReadMoreButtonProps): props is ReadMoreButtonControlledProps {
  return 'isExpanded' in props && 'onClick' in props;
}

function isSEOProps(props: ReadMoreButtonProps): props is ReadMoreButtonSEOProps {
  return 'truncatedContent' in props && 'hiddenContent' in props;
}

function isAutoSplitProps(props: ReadMoreButtonProps): props is ReadMoreButtonAutoSplitProps {
  return 'content' in props && !('maxLines' in props) && !('isExpanded' in props);
}

/**
 * Split description for SEO-friendly display
 * Tries to split at sentence boundaries, falls back to word boundaries
 */
function splitDescription(text: string, maxTruncatedLength = 150) {
  if (text.length <= maxTruncatedLength) {
    return { truncated: text, hidden: '' };
  }

  // Try to split at sentence boundary near the target length
  const sentences = text.split(/(?<=[.!?])\s+/);
  let truncatedText = '';
  let hiddenText = text;

  for (let i = 0; i < sentences.length; i++) {
    const candidate = sentences.slice(0, i + 1).join(' ');
    if (candidate.length > maxTruncatedLength) {
      break;
    }
    truncatedText = candidate;
    hiddenText = sentences.slice(i + 1).join(' ');
  }

  // If no good sentence break found, split at word boundary
  if (!truncatedText) {
    const words = text.split(/\s+/);
    let wordCount = 0;
    for (const word of words) {
      if ((truncatedText + ' ' + word).length > maxTruncatedLength) {
        break;
      }
      truncatedText += (wordCount === 0 ? '' : ' ') + word;
      wordCount++;
    }
    hiddenText = words.slice(wordCount).join(' ');
  }

  return {
    truncated: truncatedText.trim(),
    hidden: hiddenText.trim() ? ` ${hiddenText.trim()}` : '',
  };
}

/**
 * ReadMoreButton Component
 *
 * A reusable component for read more/less functionality with multiple API patterns.
 *
 * **Four Usage Patterns:**
 *
 * 1. **Auto-Split SEO (Recommended)** - Pass full content, component handles everything:
 *    ```tsx
 *    <ReadMoreButton
 *      content={fullDescription}
 *      maxTruncatedLength={150}
 *    />
 *    ```
 *
 * 2. **Manual SEO-Friendly** - Constellation V10 ShowHide pattern:
 *    ```tsx
 *    <ReadMoreButton
 *      truncatedContent="Always visible part..."
 *      hiddenContent="Hidden but in HTML source for SEO"
 *    />
 *    ```
 *
 * 3. **All-in-one** - Pass content with progressive enhancement:
 *    ```tsx
 *    <ReadMoreButton content={longText} maxLines={3} />
 *    ```
 *
 * 4. **Controlled** - Manage state externally (backward compatible):
 *    ```tsx
 *    <ReadMoreButton isExpanded={isExpanded} onClick={handleToggle} />
 *    ```
 *
 * **SEO Benefits (Pattern 1 & 2):**
 * - All content visible in HTML source for search engines
 * - No CLS (Cumulative Layout Shift) during page load
 * - User-initiated changes only (CLS-exempt)
 * - Screen readers can access full content
 * - Visual truncation with ellipses (…) to indicate more content
 *
 * **Accessibility Features:**
 * - Semantic button element with proper ARIA attributes
 * - Keyboard navigation (Enter/Space)
 * - Screen reader friendly with aria-expanded and aria-label
 * - Focus management with visible focus indicators
 */
export const ReadMoreButton = (props: ReadMoreButtonProps) => {
  // Determine which API pattern is being used first
  const isControlled = isControlledProps(props);
  const isSEO = isSEOProps(props);
  const isAutoSplit = isAutoSplitProps(props);
  const isAllInOne = !isControlled && !isSEO && !isAutoSplit;

  // Call all hooks at the top level to avoid conditional hook calls
  // Only call useState for patterns that need it (not controlled)
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [autoSplitExpanded, setAutoSplitExpanded] = useState(false);

  // Only call useReadMore for all-in-one pattern
  const allInOneProps = isAllInOne ? props : { content: '', maxLines: 2, estimatedLineLength: 75 };
  const {
    content: allInOneContent,
    maxLines = 2,
    className: allInOneClassName,
    estimatedLineLength = 75,
    characterThreshold,
    readMoreText: allInOneReadMoreText = 'Read more',
    readLessText: allInOneReadLessText = 'Read less',
    'aria-label': allInOneAriaLabel,
    disabled: allInOneDisabled = false,
  } = allInOneProps;

  const useReadMoreOptions: UseReadMoreOptions = {
    maxLines,
    estimatedLineLength,
    characterThreshold,
  };

  const {
    contentRef,
    isExpanded: allInOneIsExpanded,
    shouldShowButton,
    toggle,
    contentProps,
  } = useReadMore(allInOneContent, useReadMoreOptions);

  // Handle controlled API (existing behavior)
  if (isControlled) {
    const { isExpanded, onClick, children, disabled = false, 'aria-label': ariaLabel } = props;

    const buttonText = children || (isExpanded ? 'Read less' : 'Read more');

    return (
      <button
        type="button"
        className={readMoreButton}
        onClick={onClick}
        disabled={disabled}
        aria-expanded={isExpanded}
        aria-label={ariaLabel || (isExpanded ? 'Show less description' : 'Show more description')}
      >
        {buttonText}
        <div className={cx(caretIcon, isExpanded && caretIconRotated)}>
          <CaretDownIcon />
        </div>
      </button>
    );
  }

  // Handle SEO-friendly API (Constellation V10 ShowHide pattern)
  if (isSEO) {
    const {
      truncatedContent,
      hiddenContent,
      className,
      readMoreText = 'Read more',
      readLessText = 'Read less',
      'aria-label': ariaLabel,
      disabled = false,
    } = props;

    const handleToggle = () => {
      setSeoExpanded(!seoExpanded);
    };

    const buttonText = seoExpanded ? readLessText : readMoreText;

    // Use inline CSS function for more reliable display control
    const hiddenStyle = css({ display: seoExpanded ? 'inline' : 'none' });

    return (
      <div>
        <div className={cx(seoContentContainer, className)}>
          <span className={visibleContent}>
            {truncatedContent}
            {!seoExpanded && hiddenContent && '…'}
          </span>
          <span className={hiddenStyle}>{hiddenContent}</span>
        </div>
        <button
          type="button"
          className={readMoreButton}
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={seoExpanded}
          aria-label={ariaLabel || (seoExpanded ? `Show less content` : `Show more content`)}
        >
          {buttonText}
          <div className={cx(caretIcon, seoExpanded && caretIconRotated)}>
            <CaretDownIcon />
          </div>
        </button>
      </div>
    );
  }

  // Handle Auto-Split SEO API (Recommended)
  if (isAutoSplit) {
    const {
      content,
      maxTruncatedLength = 150,
      className,
      readMoreText = 'Read more',
      readLessText = 'Read less',
      'aria-label': ariaLabel,
      disabled = false,
    } = props;

    // Split content automatically
    const { truncated, hidden } = splitDescription(content, maxTruncatedLength);

    // If no content to hide, just render the full content
    if (!hidden) {
      return <div className={cx(seoContentContainer, className)}>{content}</div>;
    }

    const handleToggle = () => {
      setAutoSplitExpanded(!autoSplitExpanded);
    };

    const buttonText = autoSplitExpanded ? readLessText : readMoreText;

    // Use inline CSS function for more reliable display control
    const hiddenStyle = css({ display: autoSplitExpanded ? 'inline' : 'none' });

    return (
      <div>
        <div className={cx(seoContentContainer, className)}>
          <span className={visibleContent}>
            {truncated}
            {!autoSplitExpanded && '…'}
          </span>
          <span className={hiddenStyle}>{hidden}</span>
        </div>
        <button
          type="button"
          className={readMoreButton}
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={autoSplitExpanded}
          aria-label={ariaLabel || (autoSplitExpanded ? `Show less content` : `Show more content`)}
        >
          {buttonText}
          <div className={cx(caretIcon, autoSplitExpanded && caretIconRotated)}>
            <CaretDownIcon />
          </div>
        </button>
      </div>
    );
  }

  // Handle all-in-one API (new behavior with progressive enhancement)
  // If no content, render nothing
  if (!allInOneContent) {
    return null;
  }

  const buttonText = allInOneIsExpanded ? allInOneReadLessText : allInOneReadMoreText;

  return (
    <div>
      <div ref={contentRef} className={cx(readMoreContent, allInOneClassName)} {...contentProps}>
        {allInOneContent}
      </div>
      {shouldShowButton && (
        <button
          type="button"
          className={readMoreButton}
          onClick={toggle}
          disabled={allInOneDisabled}
          aria-expanded={allInOneIsExpanded}
          aria-label={allInOneAriaLabel || (allInOneIsExpanded ? `Show less content` : `Show more content`)}
        >
          {buttonText}
          <div className={cx(caretIcon, allInOneIsExpanded && caretIconRotated)}>
            <CaretDownIcon />
          </div>
        </button>
      )}
    </div>
  );
};
